import React, { useEffect, useLayoutEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import Logo from "./logo.png";
import {
  HomeNavigationState,
  useHomeNavigation,
} from "../../hooks/useHomeNavigation";
import {
  MenuContainer,
  LogoLink,
  StyledMenu,
  NavLink,
  BurgerButton,
  MobileMenuPanel,
  MobileNav,
} from "./Menu.styles";

const Menu: React.FC = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [suppressTransition, setSuppressTransition] = useState(false);
  const location = useLocation();
  const navigateToHome = useHomeNavigation();

  useLayoutEffect(() => {
    const navigationTarget = (location.state as HomeNavigationState | null)
      ?.scrollTo;
    const nextIsScrolled =
      location.pathname === "/"
        ? navigationTarget && navigationTarget !== "top"
          ? true
          : window.scrollY > 24
        : false;

    setSuppressTransition(true);
    setIsScrolled(nextIsScrolled);

    const transitionTimer = window.setTimeout(() => {
      setSuppressTransition(false);
    }, 150);

    return () => window.clearTimeout(transitionTimer);
  }, [location.key, location.pathname, location.state]);

  useEffect(() => {
    let frameId = 0;

    const updateMenuBackground = () => {
      cancelAnimationFrame(frameId);
      frameId = requestAnimationFrame(() => {
        setIsScrolled(window.scrollY > 24);
      });
    };

    updateMenuBackground();
    window.addEventListener("scroll", updateMenuBackground, { passive: true });

    return () => {
      cancelAnimationFrame(frameId);
      window.removeEventListener("scroll", updateMenuBackground);
    };
  }, []);

  useEffect(() => {
    if (!isMobileMenuOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsMobileMenuOpen(false);
    };

    const handleResize = () => {
      if (window.innerWidth > 1024) setIsMobileMenuOpen(false);
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("resize", handleResize);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("resize", handleResize);
    };
  }, [isMobileMenuOpen]);

  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  return (
    <>
      <MenuContainer
        $isScrolled={isScrolled}
        $suppressTransition={suppressTransition}
        data-site-menu
      >
        <LogoLink
          to="/"
          $compact={isScrolled}
          $suppressTransition={suppressTransition}
        >
          <img src={Logo} alt="ИРТ" />
        </LogoLink>

        <StyledMenu
          $isScrolled={isScrolled}
          $suppressTransition={suppressTransition}
        >
          <NavLink onClick={() => navigateToHome("top")}>Главная</NavLink>
          <NavLink onClick={() => navigateToHome("about")}>О нас</NavLink>
          <NavLink onClick={() => navigateToHome("products")}>Продукты</NavLink>
          <NavLink onClick={() => navigateToHome("contacts")}>Контакты</NavLink>
        </StyledMenu>

        <BurgerButton
          type="button"
          aria-label={isMobileMenuOpen ? "Закрыть меню" : "Открыть меню"}
          aria-expanded={isMobileMenuOpen}
          aria-controls="mobile-navigation"
          onClick={() => setIsMobileMenuOpen((isOpen) => !isOpen)}
          $isOpen={isMobileMenuOpen}
        >
          <span />
          <span />
          <span />
        </BurgerButton>
      </MenuContainer>

      <MobileMenuPanel
        id="mobile-navigation"
        $isOpen={isMobileMenuOpen}
        aria-hidden={!isMobileMenuOpen}
        aria-label="Мобильная навигация"
      >
        <MobileNav>
          <NavLink
            onClick={() => {
              navigateToHome("top");
              closeMobileMenu();
            }}
          >
            Главная
          </NavLink>
          <NavLink
            onClick={() => {
              navigateToHome("about");
              closeMobileMenu();
            }}
          >
            О нас
          </NavLink>
          <NavLink
            onClick={() => {
              navigateToHome("products");
              closeMobileMenu();
            }}
          >
            Продукты
          </NavLink>
          <NavLink
            onClick={() => {
              navigateToHome("contacts");
              closeMobileMenu();
            }}
          >
            Контакты
          </NavLink>
        </MobileNav>
      </MobileMenuPanel>
    </>
  );
};

export default Menu;
